// components/account/AccountHistoricsModal/components/CategorySummary.jsx
import React from 'react';
import { ProgressBar, Badge } from 'react-bootstrap';
import { 
  FaChartPie,
  FaMoneyBillWave,
  FaFile,
  FaList
} from 'react-icons/fa';
import { formatAmount } from '../../service/utils/transactionUtils';

/**
 * Résumé par catégorie - Affiche la répartition des montants par catégorie
 */
const CategorySummary = ({ categories = [], totalAmount = 0 }) => {
  /**
   * Calcule le pourcentage d'une catégorie par rapport au total
   */
  const calculatePercentage = (amount) => {
    if (totalAmount === 0) return 0;
    return Math.round((amount / totalAmount) * 100);
  };

  /**
   * Détermine la couleur de la catégorie
   */
  const getCategoryColor = (categoryName) => {
    const colors = {
      'Security': 'primary bg-opacity-10 border-primary text-white',
      'Cash': 'success bg-opacity-10 border-success text-success',
      'Depot': 'info bg-opacity-10 border-info text-info',
      'Commission Trimestrielle': 'warning bg-opacity-10 border-warning text-warning',
      'Retrait': 'danger'
    };
    
    return colors[categoryName] || 'secondary';
  };

  /**
   * Détermine l'icône de la catégorie
   */
  const getCategoryIcon = (categoryName) => {
    const icons = {
      'Security': <FaChartPie className="me-2" />,
      'Cash': <FaMoneyBillWave className="me-2" />,
      'Depot': <FaMoneyBillWave className="me-2" />,
      'Commission Trimestrielle': <FaFile className="me-2" />,
      'default': <FaList className="me-2" />
    };
    
    return icons[categoryName] || icons['default'];
  };

  if (categories.length === 0) {
    return (
      <div className="card border h-100">
        <div className="card-body text-center py-5">
          <FaChartPie className="text-muted fa-3x mb-3" />
          <p className="text-muted mb-0">Aucune donnée de catégorie disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card border h-100">
      <div className="card-header bg-light">
        <h6 className="mb-0">
          <FaChartPie className="me-2" />
          Répartition par catégorie
        </h6>
      </div>
      
      <div className="card-body">
        {/* Total général */}
        <div className="mb-4 text-center">
          <h4 className="text-primary">{formatAmount(totalAmount)}</h4>
          <small className="text-muted">Montant total toutes catégories</small>
        </div>

        {/* Liste des catégories */}
        <div className="mb-4">
          {categories.map((category, index) => {
            const percentage = calculatePercentage(category.amount);
            const color = getCategoryColor(category.category);
            
            return (
              <div key={index} className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <div className="d-flex align-items-center">
                    {getCategoryIcon(category.category)}
                    <span className="fw-medium">{category.category}</span>
                    <Badge bg={color} className="ms-2">
                      {category.count}
                    </Badge>
                    {category.hasDocuments > 0 && (
                      <Badge bg="success bg-opacity-10 border-success text-sucess" className="ms-1">
                        <FaFile size={10} className="me-1" />
                        {category.hasDocuments}
                      </Badge>
                    )}
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{formatAmount(category.amount)}</div>
                    <small className="text-muted">{percentage}%</small>
                  </div>
                </div>
                
                <ProgressBar 
                  now={percentage} 
                  variant={color}
                  className="mb-2"
                  style={{ height: '8px' }}
                />
                
                <div className="d-flex justify-content-between">
                  <small className="text-muted">
                    Moyenne: {formatAmount(category.amount / category.count)}
                  </small>
                  <small className="text-muted">
                    {category.hasDocuments}/{category.count} avec documents
                  </small>
                </div>
              </div>
            );
          })}
        </div>

        {/* Statistiques globales */}
        <div className="border-top pt-3">
          <div className="row text-center">
            <div className="col-6">
              <div className="fw-bold text-primary">
                {categories.length}
              </div>
              <small className="text-muted">Catégories</small>
            </div>
            <div className="col-6">
              <div className="fw-bold text-success">
                {categories.reduce((sum, cat) => sum + cat.hasDocuments, 0)}
              </div>
              <small className="text-muted">Documents au total</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategorySummary;